import React, { useState, useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { getApiUrl } from "../../utils/api";
import "./SettingsView.css";

function SettingsView() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [serverHealth, setServerHealth] = useState({ status: "checking", text: "Checking backend connection..." });

  const inputRefs = useRef([]);

  useEffect(() => {
    // Ping backend server
    fetch(getApiUrl("/"))
      .then((res) => res.text())
      .then((text) => {
        setServerHealth({ status: "online", text: "Online & Connected to MongoDB" });
      })
      .catch(() => {
        setServerHealth({ status: "offline", text: "Unable to connect to backend server" });
      });
  }, []);

  // Countdown timer for Resend OTP
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // 🎉 Party Popper Confetti Celebration
  const triggerPartyPopper = () => {
    try {
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 }
      });

      setTimeout(() => {
        confetti({
          particleCount: 60,
          angle: 60,
          spread: 60,
          origin: { x: 0.1, y: 0.7 }
        });
        confetti({
          particleCount: 60,
          angle: 120,
          spread: 60,
          origin: { x: 0.9, y: 0.7 }
        });
      }, 200);
    } catch (e) {
      console.error("Confetti trigger error:", e);
    }
  };

  // 1. Send OTP to Admin Registered Email
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setMessage({ type: "", text: "" });

    if (!currentPassword || !newPassword || !confirmPassword) {
      setMessage({ type: "error", text: "All password fields are required to request OTP." });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: "error", text: "New passwords do not match." });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: "error", text: "New password must be at least 6 characters long." });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(getApiUrl("/admin/auth/send-password-otp"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword })
      });

      const data = await res.json();

      if (data.success) {
        setOtpSent(true);
        setMaskedEmail(data.emailMasked || "registered email");
        setOtpDigits(["", "", "", "", "", ""]);
        setResendCooldown(30);
        setMessage({
          type: "success",
          text: `📩 OTP sent to your registered email (${data.emailMasked || "admin email"})! Enter the 6-digit code below.`
        });
        setTimeout(() => {
          if (inputRefs.current[0]) inputRefs.current[0].focus();
        }, 150);
      } else {
        setMessage({ type: "error", text: `❌ ${data.message || "Failed to send OTP."}` });
      }
    } catch (err) {
      console.error("Error sending OTP:", err);
      setMessage({ type: "error", text: "❌ Server error while sending OTP. Please check connection." });
    } finally {
      setLoading(false);
    }
  };

  // 2. Auto-Verify OTP & Change Password (NO verify button needed)
  const handleAutoVerify = async (code) => {
    if (verifying) return;
    setVerifying(true);
    setMessage({ type: "", text: "" });

    try {
      const token = localStorage.getItem("adminToken");
      const res = await fetch(getApiUrl("/admin/auth/change-password"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          otp: code
        })
      });

      const data = await res.json();

      if (data.success) {
        triggerPartyPopper();
        setMessage({
          type: "success",
          text: "🎉 Password changed successfully! Use your new password on next login."
        });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setOtpDigits(["", "", "", "", "", ""]);
        setOtpSent(false);
      } else {
        setMessage({ type: "error", text: `❌ ${data.message || "Invalid or expired OTP."}` });
        setOtpDigits(["", "", "", "", "", ""]);
        setTimeout(() => {
          if (inputRefs.current[0]) inputRefs.current[0].focus();
        }, 100);
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setMessage({ type: "error", text: "❌ Server error while changing password." });
      setOtpDigits(["", "", "", "", "", ""]);
    } finally {
      setVerifying(false);
    }
  };

  // Handle single digit input with auto-advance
  const handleDigitChange = (index, value) => {
    const cleaned = value.replace(/\D/g, "");
    if (!cleaned) {
      const newDigits = [...otpDigits];
      newDigits[index] = "";
      setOtpDigits(newDigits);
      return;
    }

    const digit = cleaned[cleaned.length - 1];
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);

    // Advance focus to next box
    if (index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }

    // Auto verify when all 6 digits are provided
    const fullCode = newDigits.join("");
    if (fullCode.length === 6 && !newDigits.includes("")) {
      handleAutoVerify(fullCode);
    }
  };

  // Handle Backspace navigation across boxes
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (!otpDigits[index] && index > 0 && inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    }
  };

  // Handle Paste of complete 6-digit OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pastedData) return;

    const newDigits = [...otpDigits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pastedData[i] || "";
    }
    setOtpDigits(newDigits);

    if (pastedData.length === 6) {
      handleAutoVerify(pastedData);
    } else {
      const nextFocus = Math.min(pastedData.length, 5);
      if (inputRefs.current[nextFocus]) {
        inputRefs.current[nextFocus].focus();
      }
    }
  };

  return (
    <div className="settings-view-container">
      <div className="page-header-row">
        <div>
          <h2>⚙️ Admin Settings & Security</h2>
          <p className="page-sub">Manage credentials, API configuration, and server connections</p>
        </div>
      </div>

      <div className="dash-two-col">
        {/* CHANGE PASSWORD PANEL */}
        <div className="dash-glass-panel">
          <h2>🔐 Change Admin Password</h2>
          <p className="settings-panel-sub">Update your account password with email OTP authorization</p>

          {message.text && (
            <div className={`settings-alert ${message.type === "success" ? "alert-success" : "alert-error"}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSendOtp} className="settings-form">
            <div className="input-group">
              <label>Current Password</label>
              <input
                type="password"
                placeholder="Enter Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={loading || verifying}
                required
              />
            </div>

            <div className="input-group">
              <label>New Password</label>
              <input
                type="password"
                placeholder="Enter New Password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading || verifying}
                required
              />
            </div>

            <div className="input-group">
              <label>Confirm New Password</label>
              <input
                type="password"
                placeholder="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading || verifying}
                required
              />
            </div>

            {!otpSent ? (
              <button
                type="submit"
                className="login-submit-btn send-otp-btn"
                disabled={loading || verifying}
              >
                {loading ? "Sending OTP to Registered Email... ⏳" : "Send OTP 📩"}
              </button>
            ) : (
              <div className="otp-verification-box glass-glow">
                <div className="otp-box-header">
                  <span className="otp-icon">🔐</span>
                  <div>
                    <h4>Enter 6-Digit OTP</h4>
                    <p className="otp-subtext">
                      Sent to registered email (<b>{maskedEmail}</b>). Auto-verifies upon 6th digit.
                    </p>
                  </div>
                </div>

                <div className="otp-digits-container">
                  {otpDigits.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => (inputRefs.current[idx] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleDigitChange(idx, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      onPaste={handlePaste}
                      disabled={verifying}
                      className={`otp-digit-input ${digit ? "filled" : ""}`}
                    />
                  ))}
                </div>

                {verifying && (
                  <div className="otp-verifying-status">
                    <div className="mini-spinner"></div>
                    <span>Auto-verifying OTP & updating password...</span>
                  </div>
                )}

                <div className="otp-actions-row">
                  <button
                    type="button"
                    className="btn-resend-otp"
                    onClick={handleSendOtp}
                    disabled={resendCooldown > 0 || verifying || loading}
                  >
                    {resendCooldown > 0 ? `⏳ Resend OTP (${resendCooldown}s)` : "🔄 Resend OTP"}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* SYSTEM HEALTH & CREDENTIALS INFO */}
        <div className="dash-glass-panel">
          <h2>🖥️ System Diagnostics & API Status</h2>
          
          <div className="health-card">
            <div className="health-header">
              <span className="health-label">Backend Connection</span>
              <span className={`health-badge ${serverHealth.status}`}>
                {serverHealth.status === "online" ? "🟢 ONLINE" : serverHealth.status === "offline" ? "🔴 OFFLINE" : "🟡 CHECKING"}
              </span>
            </div>
            <p className="health-text">{serverHealth.text}</p>
          </div>

          <div className="system-info-list">
            <div className="sys-info-item">
              <span className="sys-key">Authentication Engine:</span>
              <span className="sys-val">JWT (JSON Web Token)</span>
            </div>
            <div className="sys-info-item">
              <span className="sys-key">Password Encryption:</span>
              <span className="sys-val">bcryptjs (Salt 10 Rounds)</span>
            </div>
            <div className="sys-info-item">
              <span className="sys-key">Primary Database:</span>
              <span className="sys-val">MongoDB Atlas</span>
            </div>
            <div className="sys-info-item">
              <span className="sys-key">Analytics Engine:</span>
              <span className="sys-val">Firebase Firestore</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsView;
